import React, { useEffect } from 'react';
import { Typography, Grid, Paper } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useA11ySettings } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@a11y/A11ySettings/state/use-a11y-settings';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import { updateUserSetting } from '@monorepo/shared/src/api';
import { FONT_GSS_FUTURA_SEMI_BOLD } from '@monorepo/shared/src/themes/defaultTheme';
import useUserStore from '@monorepo/shared/src/graph/hooks/use-user';
import { getSetting } from 'graph/selectors/setting-selector';
import AudioControls from './AudioControls';
import ClosedCaptions from './ClosedCaptions';

const t = (key: string, options?) => translate('preferences', key, options);
const SHOW_CLOSED_CAPTIONS = 'showClosedCaptions';
const DISPLAY__CC_CONTROL = 'displayCCControl';

const PreferencesAccessibility: React.FC = () => {
  const [state, { onScalingChange, onToggleChange, save }] = useA11ySettings();
  const [user, { userRefetch }] = useUserStore();
  const classes = useStyles();

  const handleToggle = (settingName, settingType) => async () => {
    const newState = await onToggleChange(settingName, settingType)(settingName);

    if (settingType === SHOW_CLOSED_CAPTIONS) {
      newState[settingName][DISPLAY__CC_CONTROL] = newState[settingName][SHOW_CLOSED_CAPTIONS];
    }
    await updateUserSetting('accessibility', {
      accessibility: newState ?? state,
    });
    await userRefetch();
  };

  const handleScaling = (settingName, settingType) => async (stepScaling, min, max) => {
    const newState = await onScalingChange(settingName, settingType)(stepScaling, min, max);
    await updateUserSetting('accessibility', {
      accessibility: newState ?? state,
    });
    await userRefetch();
  };

  useEffect(() => {
    const accessibilitySettings = getSetting(user?.me?.settings, 'accessibility');
    if (accessibilitySettings?.value) {
      save(accessibilitySettings?.value);
    }
  }, [user?.me?.settings]);

  return (
    <Grid item xs={12}>
      <Paper>
        <Grid container>
          <Grid item xs={12} className={classes.gridItem}>
            <Typography variant="h2" className={classes.header}>
              {t('accessibility')}
            </Typography>
            <Typography variant="body1" className={classes.subtitle}>
              {t('accessibilitySubTitle')}
            </Typography>
          </Grid>
          <ClosedCaptions
            handleScaling={handleScaling}
            handleToggle={handleToggle}
            closedCaptionsSettings={state.closedCaptionsSettings}
          />
          <AudioControls
            handleScaling={handleScaling}
            handleToggle={handleToggle}
            audioSettings={state.audioSettings}
          />
        </Grid>
      </Paper>
    </Grid>
  );
};

const useStyles = makeStyles((theme) => ({
  header: {
    fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
    marginTop: '15px',
  },
  subtitle: {
    fontSize: '18px',
  },
  gridItem: {
    marginLeft: theme.spacing(6),
  },
  gridSection: {
    borderRadius: '5px',
    border: '1px solid gray',
  },
  gridRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
}));

export default PreferencesAccessibility;
